import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { SpendLeakageReport } from '../../lib/api';
import { toNumber } from './leakageFormat';

interface LeakageUploadSplitTilesProps {
  report: SpendLeakageReport;
  formatCurrency: (amount: number | string) => string;
}

function SplitTile({
  title,
  spend,
  leakage,
  suppliers,
  tone,
  formatCurrency,
}: {
  title: string;
  spend: number | string | undefined;
  leakage: number | string | undefined;
  suppliers: number | undefined;
  tone: 'good' | 'bad';
  formatCurrency: (amount: number | string) => string;
}) {
  const Icon = tone === 'good' ? CheckCircle2 : XCircle;
  const spendNum = toNumber(spend);
  const leakNum = toNumber(leakage);
  const pct = spendNum > 0 ? (leakNum / spendNum) * 100 : 0;
  return (
    <Card className={tone === 'good' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className={`text-sm font-medium ${tone === 'good' ? 'text-green-700' : 'text-red-700'}`}>{title}</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-500">Spend</p>
                <p className="text-lg font-bold">{formatCurrency(spendNum)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Leakage</p>
                <p className={`text-lg font-bold ${tone === 'good' ? '' : 'text-red-700'}`}>{formatCurrency(leakNum)}</p>
                <p className="text-xs text-gray-500">{pct.toFixed(1)}% of spend</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Suppliers</p>
                <p className="text-lg font-bold">{suppliers !== undefined ? suppliers.toLocaleString() : '—'}</p>
              </div>
            </div>
          </div>
          <Icon className={`h-8 w-8 ${tone === 'good' ? 'text-green-500' : 'text-red-500'}`} />
        </div>
      </CardContent>
    </Card>
  );
}

/** Spend / leakage / supplier count, split by whether the supplier uploaded its monthly file. */
export default function LeakageUploadSplitTiles({ report, formatCurrency }: LeakageUploadSplitTilesProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="leakage-upload-split">
      <SplitTile
        title="Suppliers who uploaded"
        spend={report.uploaded_spend}
        leakage={report.uploaded_leakage}
        suppliers={report.uploaded_supplier_count}
        tone="good"
        formatCurrency={formatCurrency}
      />
      <SplitTile
        title="Suppliers who did not upload"
        spend={report.not_uploaded_spend}
        leakage={report.not_uploaded_leakage}
        suppliers={report.not_uploaded_supplier_count}
        tone="bad"
        formatCurrency={formatCurrency}
      />
    </div>
  );
}
